import $ from 'jquery';
import _ from 'underscore';

var Legacy = Legacy || {};

interface IPersonName {
    prefix: string | null,
    first: string | null,
    middle: string | null,
    last: string | null,
    suffix: string | null,
};

export class PersonName implements IPersonName {
  public prefix: string | null;
  public first: string | null;
  public middle: string | null;
  public last: string | null;
  public suffix: string | null;

  public constructor(prefix: string, first: string, middle: string, last: string, suffix: string) {
    this.prefix = prefix;
    this.first = first;
    this.middle = middle;
    this.last = last;
    this.suffix = suffix;
  }

  public static parse(input: string): PersonName {
    if (!input || input.isNullOrWhitespace())
      return new PersonName(null, null, null, null, null);

    let prefixes = ["Dr.", "Miss", "Mr.", "Mrs.", "Ms.", "Prof."];
    let suffixes = ["I", "II", "III", "Jr.", "Sr.", "IV", "V", "VI", "VII", "VIII", "IX", "Esq."];
    let lastNamePrefixes = ["Al", "Au", "De", "Di", "Du", "El", "La", "Le", "St", "Van", "Von"];

    let values: IPersonName = {
      prefix: null,
      first: null,
      middle: null,
      last: null,
      suffix: null,
    };

    if (isFormalForm(input)) {
      parseFormal(input, values);
    } else {
      parseInformal(input, values);
    }

    // capitalize all parts
    if (values.prefix)
      values.prefix = values.prefix.capitalize();
    if (values.first)
      values.first = values.first.capitalize();
    if (values.middle)
      values.middle = values.middle.capitalize();
    if (values.last)
      values.last = values.last.capitalize();
    if (values.suffix)
      values.suffix = values.suffix.capitalize();

    return new PersonName(
      values.prefix,
      values.first,
      values.middle,
      values.last,
      values.suffix
    );

    function parseFormal(name: string, result: IPersonName) {
      let commaIndex = name.indexOf(',');
      let beforeComma = name.substring(0, commaIndex);
      let afterComma = name.substring(commaIndex + 1);

      result.last = beforeComma;

      if (!afterComma || afterComma?.isNullOrWhitespace())
        return;               

      let words = afterComma.split(/\s*,?\s+|,/).filter(x => x && !x.isNullOrWhitespace());

      extractPrefix(words, result);
      extractSuffix(words, result);
      extractFirstNameSingleCharacter();
      extractMiddleInitial();

      result.first = result.first || words.join(" ");

      function extractFirstNameSingleCharacter(): void {
        if (words.length == 0)
          return;

        var testWord = words[0];
        if (testWord.length == 1) {
          result.first = testWord;
          words.shift();
        }
      }

      function extractMiddleInitial(): void {
        if (words.length == 0)
          return;

        if (words.length == 2 && isLastNamePrefix(words[0]))
          return;

        var testWord = last(words);
        if (testWord.length == 1 || testWord.length == 2 && testWord[1] == '.') {
          result.middle = trimDot(testWord);
          words.pop();
        }
      }
    }

    function parseInformal(name: string, result: IPersonName) {
      let words = name.trim().split(/\s*,?\s+|,/).filter(x => x && !x.isNullOrWhitespace());

      extractPrefix(words, result);
      extractSuffix(words, result);

      if (words.length == 0)
        return;
      else if (words.length == 1) {
        result.last = words[0];
        return;
      }

      if (words.length == 2) {
        if (isLastNamePrefix(words[0])) {
          result.last = words.join(" ");
          return;
        }

        result.first = words[0];
        result.last = words[1];

        return;
      }

      if (words.length == 3) {

        if (isInitial(words[1])) {
          result.first = words[0];
          result.middle = trimDot(words[1]);
          result.last = words[2];
          return;
        }

        if (isLastNamePrefix(words[1])) {
          result.first = words[0];
          result.last = `${words[1]} ${words[2]}`;
          return;
        }

        result.first = `${words[0]} ${words[1]}`;
        result.last = words[2];

        return;
      }

      let isFirst = true;
      let foundLastToken = false;
      let firstWords: string[] = [];
      let lastWords: string[] = [];

      while (words.length > 0) {
        let testWord = words[0];

        if (!isFirst && isInitial(testWord))
          result.middle = testWord[0];
        else if (isFirst) {
          firstWords.push(testWord);
          isFirst = false;
        }
        else if (!foundLastToken && isLastNamePrefix(testWord)) {
          firstWords.push(...lastWords);
          lastWords = [testWord];
          foundLastToken = true;
        }
        else
          lastWords.push(testWord);

        words.shift();
      }

      result.first = firstWords.join(" ");
      result.last = lastWords.join(" ");
    }

    function isInitial(word: string): boolean {
      return word.length == 1 || (word.length == 2 && word[1] == '.');
    }

    function isLastNamePrefix(word: string): boolean {
      return lastNamePrefixes.some(x => isEqualIgnoreCase(x, word));
    }

    function extractPrefix(words: string[], result: IPersonName) {
      if (prefixes == null || words.length == 0)
        return;

      let foundPrefix = findWord(prefixes, words[0]);

      if (foundPrefix) {
        result.prefix = foundPrefix;
        words.shift();
      }
    }

    function extractSuffix(words: string[], result: IPersonName) {
      if (suffixes == null || words.length == 0)
        return;

      let foundSuffix = findWord(suffixes, last(words));

      if (foundSuffix) {
        result.suffix = foundSuffix;
        words.pop();
      }
    }

    function isFormalForm(name: string): boolean {
      let suffixFollowsComma = false;
      let commaIndex = name.indexOf(',');

      if (commaIndex > -1) {
        let afterComma = name.substring(commaIndex + 1).trim();
        suffixFollowsComma = findWord(suffixes, afterComma) != null;
      }

      let hasCommaNotFollowedBySuffix = commaIndex > -1 && !suffixFollowsComma;
      return hasCommaNotFollowedBySuffix;
    }

    function findWord(words: string[], testword: string): string | null {
      if (words == null || !testword || testword.isNullOrWhitespace())
        return null;

      return _.find(words, x => isEqualIgnoreCase(x, testword) || isEqualIgnoreCase(x, testword + "."));
    }

    function last<T>(array: T[]): T {
      return array[array.length - 1];
    }

    function isEqualIgnoreCase(a: string, b: string): boolean {
      return a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0;
    }

    function trimDot(value: string): string {
      return value.replace('.', '');
    }
  }

  public format(): string | null {
    let middle = this.middle;
    if (middle && middle.length == 1)
      middle += '.';

    let nocomma = PersonName.joinNotNullOrWhitespace(" ", this.prefix, this.first, middle);
    let name = PersonName.joinNotNullOrWhitespace(", ", this.last, nocomma, this.suffix);
    return !name || name.isNullOrWhitespace() ? null : name;
  }

  public formatInformal(): string | null {
    let middle = this.middle;
    if (middle && middle.length == 1)
      middle += '.';

    let name = PersonName.joinNotNullOrWhitespace(" ", this.prefix, this.first, middle, this.last);
    if (!this.suffix.isNullOrWhitespace())
      name = name + ", " + this.suffix;

    return !name || name.isNullOrWhitespace() ? null : name;
  }

  private static joinNotNullOrWhitespace = function (separator: string, ...args: string[]): string {
    return args.filter(x => x && !x.isNullOrWhitespace()).join(separator);
  }
}

export default PersonName;
